import React, { useState, useMemo } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const MonthlyAttendanceHeatmap = ({ data, year, month }) => {
  const [currentYear, setCurrentYear] = useState(year || new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(month || new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);

  // Process attendance data for the month
  const attendanceData = useMemo(() => {
    if (!data?.data?.attendances) return {};

    const attendancesByDate = {};
    
    data.data.attendances.forEach(attendance => {
      const date = attendance.attendance_date.split('T')[0];
      if (!attendancesByDate[date]) {
        attendancesByDate[date] = [];
      }
      attendancesByDate[date].push(attendance);
    });

    return attendancesByDate;
  }, [data]);

  // Calculate statistics for each day
  const getDayStats = (date) => {
    const dayAttendances = attendanceData[date] || [];
    if (dayAttendances.length === 0) return null;

    const totalEmployees = dayAttendances.length;
    const presentCount = dayAttendances.filter(a => a.status === 'present').length;
    const lateCount = dayAttendances.filter(a => a.status === 'late').length;
    const absentCount = dayAttendances.filter(a => a.status === 'absent').length;
    const totalHours = dayAttendances.reduce((sum, a) => sum + (a.net_working_minutes / 60), 0);
    
    // Determine overall status
    let status = 'mixed';
    if (presentCount === totalEmployees) status = 'full';
    else if (lateCount > 0 && presentCount + lateCount === totalEmployees) status = 'mixed';
    else if (presentCount === 0 && lateCount === 0) status = 'empty';
    
    return {
      totalEmployees,
      presentCount,
      lateCount,
      absentCount,
      totalHours: totalHours.toFixed(1),
      averageHours: (totalHours / totalEmployees).toFixed(1),
      status,
      attendanceRate: ((presentCount + lateCount) / totalEmployees * 100).toFixed(1),
      records: dayAttendances
    };
  };

  // Generate calendar days for the month
  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    const days = [];
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add actual days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        date,
        day: i,
        stats: getDayStats(date)
      });
    }
    
    return days;
  };

  const getHeatmapColor = (stats) => {
    if (!stats) return 'bg-gray-100 hover:bg-gray-200';
    
    const rate = parseFloat(stats.attendanceRate);
    if (rate >= 90) return 'bg-green-500 hover:bg-green-600 text-white';
    if (rate >= 75) return 'bg-green-400 hover:bg-green-500 text-white';
    if (rate >= 60) return 'bg-yellow-400 hover:bg-yellow-500';
    if (rate >= 40) return 'bg-orange-400 hover:bg-orange-500';
    if (rate >= 20) return 'bg-red-400 hover:bg-red-500';
    return 'bg-red-600 hover:bg-red-700 text-white';
  };

  const getIntensityColor = (stats) => {
    if (!stats) return 'bg-gray-100';
    
    const hours = parseFloat(stats.averageHours);
    if (hours >= 8) return 'bg-green-500';
    if (hours >= 6) return 'bg-green-400';
    if (hours >= 4) return 'bg-yellow-400';
    if (hours >= 2) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const days = getDaysInMonth(currentYear, currentMonth);
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Calculate monthly statistics
  const monthlyStats = useMemo(() => {
    const allDays = days.filter(d => d !== null && d.stats !== null);
    if (allDays.length === 0) return null;

    const totalWorkingDays = allDays.length;
    const totalEmployeeDays = allDays.reduce((sum, d) => sum + (d.stats?.totalEmployees || 0), 0);
    const totalPresent = allDays.reduce((sum, d) => sum + (d.stats?.presentCount || 0), 0);
    const totalLate = allDays.reduce((sum, d) => sum + (d.stats?.lateCount || 0), 0);
    const totalAbsent = allDays.reduce((sum, d) => sum + (d.stats?.absentCount || 0), 0);
    const totalHours = allDays.reduce((sum, d) => sum + parseFloat(d.stats?.totalHours || 0), 0);
    
    const avgAttendanceRate = totalEmployeeDays > 0 
      ? ((totalPresent + totalLate) / totalEmployeeDays * 100).toFixed(1)
      : 0;
    
    const avgWorkingHours = totalEmployeeDays > 0 
      ? (totalHours / totalEmployeeDays).toFixed(1)
      : 0;

    return {
      totalWorkingDays,
      totalEmployeeDays,
      totalPresent,
      totalLate,
      totalAbsent,
      totalHours: totalHours.toFixed(1),
      avgAttendanceRate,
      avgWorkingHours,
      attendanceRate: avgAttendanceRate
    };
  }, [days]);

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDate(null);
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{title}</p>
          <p className="text-xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-full ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header with Month Navigation */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-800">Attendance Heatmap</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-700">
              {monthNames[currentMonth]} {currentYear}
            </h2>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Monthly Summary Stats */}
        {monthlyStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <StatCard
              title="Working Days"
              value={monthlyStats.totalWorkingDays}
              icon={Calendar}
              color="bg-blue-500"
            />
            <StatCard
              title="Employee Days"
              value={monthlyStats.totalEmployeeDays}
              icon={Users}
              color="bg-purple-500"
            />
            <StatCard
              title="Attendance Rate"
              value={`${monthlyStats.avgAttendanceRate}%`}
              icon={TrendingUp}
              color="bg-green-500"
            />
            <StatCard
              title="Present"
              value={monthlyStats.totalPresent}
              icon={CheckCircle}
              color="bg-green-500"
            />
            <StatCard
              title="Late"
              value={monthlyStats.totalLate}
              icon={AlertCircle}
              color="bg-yellow-500"
            />
            <StatCard
              title="Absent"
              value={monthlyStats.totalAbsent}
              icon={XCircle}
              color="bg-red-500"
            />
            <StatCard
              title="Avg Hours/Day"
              value={`${monthlyStats.avgWorkingHours}h`}
              icon={Clock}
              color="bg-indigo-500"
            />
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Attendance Rate:</span>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-green-500 rounded"></div>
              <span className="text-xs">≥90%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-green-400 rounded"></div>
              <span className="text-xs">75-89%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-yellow-400 rounded"></div>
              <span className="text-xs">60-74%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-orange-400 rounded"></div>
              <span className="text-xs">40-59%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-red-400 rounded"></div>
              <span className="text-xs">20-39%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-red-600 rounded"></div>
              <span className="text-xs">&lt;20%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 bg-gray-100 rounded border"></div>
              <span className="text-xs">No Data</span>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Click on any day to view detailed attendance
          </div>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="bg-white rounded-lg shadow-md p-6 overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekdays.map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="aspect-square bg-gray-50 rounded-lg"></div>;
              }
              
              const isSelected = selectedDate === day.date;
              const isHovered = hoveredDate === day.date;
              
              return (
                <div
                  key={day.date}
                  className={`
                    relative aspect-square rounded-lg cursor-pointer transition-all duration-200
                    ${getHeatmapColor(day.stats)}
                    ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 scale-105' : ''}
                    ${isHovered ? 'scale-105 shadow-lg' : ''}
                  `}
                  onClick={() => setSelectedDate(isSelected ? null : day.date)}
                  onMouseEnter={() => setHoveredDate(day.date)}
                  onMouseLeave={() => setHoveredDate(null)}
                >
                  <div className="absolute top-1 left-1 text-xs font-semibold">
                    {day.day}
                  </div>
                  {day.stats && (
                    <div className="absolute bottom-1 right-1 text-xs font-medium">
                      {day.stats.attendanceRate}%
                    </div>
                  )}
                  {day.stats && day.stats.records.length > 1 && (
                    <div className="absolute top-1 right-1">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selected Day Details */}
      {selectedDate && (
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">
              {new Date(selectedDate).toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          {attendanceData[selectedDate] ? (
            <div>
              {/* Day Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Total Employees</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {attendanceData[selectedDate].length}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Present</p>
                  <p className="text-2xl font-bold text-green-600">
                    {attendanceData[selectedDate].filter(a => a.status === 'present').length}
                  </p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Late</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {attendanceData[selectedDate].filter(a => a.status === 'late').length}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Absent</p>
                  <p className="text-2xl font-bold text-red-600">
                    {attendanceData[selectedDate].filter(a => a.status === 'absent').length}
                  </p>
                </div>
              </div>

              {/* Employee List */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Shift</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Clock In/Out</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendanceData[selectedDate].map((attendance) => (
                      <tr key={attendance.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm">
                          {attendance.user?.first_name} {attendance.user?.last_name}
                        </td>
                        <td className="px-4 py-2 text-sm">{attendance.shift?.name}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            attendance.status === 'present' ? 'bg-green-100 text-green-600' :
                            attendance.status === 'late' ? 'bg-yellow-100 text-yellow-600' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {attendance.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {attendance.clock_in?.substring(11, 16)} - {attendance.clock_out?.substring(11, 16)}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {(attendance.net_working_minutes / 60).toFixed(1)}h
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {attendance.clock_in_location_name || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No attendance records for this date
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {(!monthlyStats || monthlyStats.totalWorkingDays === 0) && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center mt-6">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No attendance data</h3>
          <p className="text-gray-500">No attendance records found for {monthNames[currentMonth]} {currentYear}</p>
        </div>
      )}
    </div>
  );
};

export default MonthlyAttendanceHeatmap;